import { useCallback } from "react";
import { useMutation } from "react-query";
import { CheckInVerificationStatus, Guest, GuestStatus, Purchase, PurchaseItem, Tag, TagId } from "types";
import { api } from "@/api/api";

export enum UpdateTagAction {
    ADD = "add",
    REMOVE = "remove",
}

export interface UpdateTagsPayload {
    purchaseId: ID;
    tag: Tag;
    action: UpdateTagAction;
}

export const useVerificationTags = (purchase?: Purchase, purchaseItem?: PurchaseItem) => {
    const { mutateAsync: updateTags } = useMutation(async ({ action, purchaseId, tag }: UpdateTagsPayload) => {
        return action === UpdateTagAction.ADD
            ? api.post(`/purchases/${purchaseId}/tags`, tag)
            : api.delete(`/purchases/${purchaseId}/tags/${encodeURIComponent(tag.id)}`);
    });

    const updateVerificationTags = useCallback(
        async (action: UpdateTagAction, verificationStatus?: CheckInVerificationStatus) => {
            if (!purchase || !purchaseItem) return;

            const verificationTag = getIdentityVerificationTag(purchase.tags ?? [], purchaseItem);
            if (action === UpdateTagAction.REMOVE) {
                if (!verificationTag) return;

                await updateTags({ purchaseId: purchase.id, action: UpdateTagAction.REMOVE, tag: verificationTag });
                return;
            }

            let tagId: string =
                verificationStatus === CheckInVerificationStatus.ID_VERIFIED
                    ? TagId.ID_VERIFIED
                    : TagId.ID_NOT_VERIFIED;
            tagId += ` (${purchaseItem.name}) #${purchaseItem.id}`;

            await updateTags({
                purchaseId: purchase.id,
                action: UpdateTagAction.ADD,
                tag: {
                    id: tagId,
                },
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [purchase?.tags],
    );

    return { updateVerificationTags };
};

const getIdentityVerificationTag = (tags: Tag[], purchaseItem: PurchaseItem) => {
    return tags.find((tag) => {
        if (tag.id.startsWith(TagId.ID_VERIFIED) || tag.id.startsWith(TagId.ID_NOT_VERIFIED)) {
            return new RegExp(` #${purchaseItem.id}$`).test(tag.id);
        }

        return false;
    });
};

export const getVerificationTagUpdateAction = (
    guestStatus: GuestStatus,
    guests: Guest[],
    verificationStatus?: CheckInVerificationStatus,
) => {
    if (guestStatus === GuestStatus.ARRIVED) {
        return verificationStatus ? UpdateTagAction.ADD : null;
    }

    if (guestStatus === GuestStatus.PENDING || guestStatus === GuestStatus.NO_SHOW) {
        const hasVerificationStatus = guests.some((guest) => {
            return guest.checkIns?.some((checkIn) => !checkIn.deletedAt && checkIn.verificationStatus);
        });
        return hasVerificationStatus ? UpdateTagAction.REMOVE : null;
    }

    return null;
};
